import {
  useModalContext,
  WSButton,
  WSDrawer,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSLayout,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IMemberClient,
  MemberClientStatus
} from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo, useState } from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import { OverlaySpinner } from "../../components/OverlaySpinner";
import { SidePanelSection } from "../../components/SidePanel";
import { Tooltip } from "../../components/Tooltip";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import {
  useIntegrationsQuickbooks,
  useIntegrationsQuickbooksCustomers,
  useIntegrationsQuickbooksItems
} from "../../query/integrations/queries";
import { isQuickbooksServiceActive } from "../../query/integrations/selectors";
import { useUpdateMemberClient } from "../../query/payments/mutations";
import { useMemberClientQuery } from "../../query/payments/queries";
import {
  getClientCompany,
  getClientEmail,
  getClientName
} from "../../query/payments/selectors";
import { WSQueries } from "../../query/WSQuery";
import { getIntegrationUserSelectLabel } from "../../Settings/screens/Integrations/quickbooks/QuickbooksSetupStep6";
import { copyText } from "../../utils/copyText";
import { getParentPath } from "../../utils/goToParentRoute";
import { ClientEligibilityRequirements } from "../components/ClientEligibilityRequirements/ClientEligibilityRequirements";
import DocumentsSection from "../components/DocumentsSection";
import getDocumentsMetaFromProfile from "../components/DocumentsSection/getDocumentsMetaFromProfile";
import {
  EditClientIntegrationsModal,
  EDIT_CLIENT_INTEGRATIONS_MODAL
} from "../components/EditClientIntegrationsModal/EditClientIntegrationsModal";
import {
  EditContactInfoModal,
  EDIT_CONTACT_INFO_MODAL
} from "../components/EditContactInfoModal/EditContactInfoModal";
import {
  EditContactNameModal,
  EDIT_CONTACT_NAME_MODAL
} from "../components/EditContactNameModal/EditContactNameModal";
import { QuickbooksHistory } from "../components/QuickbooksHistory/QuickbooksHistory";
import styles from "./InvoicesContactDetails.module.scss";

type Props = RouteComponentProps<
  { memberClientId: string },
  {},
  { backPath: string }
>;

export const InvoicesClientDetails: React.FC<Props> = ({
  match,
  location,
  history
}) => {
  const backPath = location.state?.backPath || getParentPath(history);
  const memberClientQuery = useMemberClientQuery(match.params.memberClientId);

  return (
    <WSQueries
      queries={{ memberClientQuery }}
      renderLoader={() => (
        <>
          <WSScreen.Mobile>
            <OverlaySpinner />
          </WSScreen.Mobile>
          <WSScreen.TabletAndDesktop>
            <WSDrawer
              onClose={() => {
                history.push(backPath);
              }}
              size="S"
              noCloseIcon
            >
              <WSElement className={styles.main}>
                <OverlaySpinner />
              </WSElement>
            </WSDrawer>
          </WSScreen.TabletAndDesktop>
        </>
      )}
    >
      {({ memberClientQuery: { data: memberClient } }) => (
        <>
          <WSScreen.Mobile>
            <WSLayout
              title={
                getClientName(memberClient) || getClientEmail(memberClient)
              }
              onBack={() => {
                history.push(backPath);
              }}
              line
            >
              <WSElement className={styles.main}>
                <Inner memberClient={memberClient} backPath={backPath} />
              </WSElement>
            </WSLayout>
          </WSScreen.Mobile>
          <WSScreen.TabletAndDesktop>
            <WSDrawer
              onClose={() => {
                history.push(backPath);
              }}
              size="S"
              noCloseIcon
            >
              <WSLayout
                title={
                  getClientName(memberClient) || getClientEmail(memberClient)
                }
                onClose={() => {
                  history.push(backPath);
                }}
                line
                smallHeader
              >
                <WSElement className={styles.main}>
                  <Inner memberClient={memberClient} backPath={backPath} />
                </WSElement>
              </WSLayout>
            </WSDrawer>
          </WSScreen.TabletAndDesktop>
        </>
      )}
    </WSQueries>
  );
};

const Inner: React.FC<{ memberClient: IMemberClient; backPath: string }> = ({
  memberClient,
  backPath
}) => {
  const history = useHistory();
  const location = useLocation();
  const { openModal } = useModalContext();
  const [updateMemberClient, updateMemberClientMeta] = useUpdateMemberClient(
    memberClient.memberClientId
  );
  const [
    isInternationalBankDetailsVisible,
    setIsInternationalBankDetailsVisible
  ] = useState(false);

  const company = getClientCompany(memberClient);
  const name = getClientName(memberClient);

  const qFeatureFlags = useFeatureFlags();

  const qIntegrationsQuickbooks = useIntegrationsQuickbooks();
  const qIntegrationsQuickbooksCustomers = useIntegrationsQuickbooksCustomers();
  const qIntegrationsQuickbooksItems = useIntegrationsQuickbooksItems();

  const isQuickbooksActive =
    qFeatureFlags.data?.qboContactsControl &&
    qIntegrationsQuickbooks.data &&
    isQuickbooksServiceActive(qIntegrationsQuickbooks.data);

  const defaultItem = (qIntegrationsQuickbooksItems.data || []).find(
    item => item.itemId === qIntegrationsQuickbooks.data?.defaults?.itemId
  );

  const mappedCustomer = (qIntegrationsQuickbooksCustomers.data || []).find(
    customer =>
      memberClient.integration?.quickbooks?.customerId === customer.customerId
  );
  const mappedItem = (qIntegrationsQuickbooksItems.data || []).find(
    item => memberClient.integration?.quickbooks?.itemId === item.itemId
  );

  const documents = useMemo(() => getDocumentsMetaFromProfile(memberClient), [
    memberClient
  ]);

  return (
    <>
      <SidePanelSection first>
        <WSFlexBox.CenterY wrap="nowrap" justify="space-between" mb="S">
          <WSText.Heading3 data-testid="clientName">{name}</WSText.Heading3>
          <WSButton.Link
            name="editClientName"
            onClick={() => {
              openModal(EDIT_CONTACT_NAME_MODAL);
            }}
          >
            Edit
          </WSButton.Link>
        </WSFlexBox.CenterY>

        {company && <WSText.Heading4 mb="M">{company}</WSText.Heading4>}

        <WSButton.Secondary
          onClick={() => {
            history.push("/member/invoices/create", {
              backPath: location.pathname,
              memberClientId: memberClient.memberClientId
            });
          }}
          fullWidth
        >
          Create invoice
        </WSButton.Secondary>
      </SidePanelSection>

      <SidePanelSection>
        <WSFlexBox.CenterY wrap="nowrap" justify="space-between" mb="S">
          <WSText.Heading3>Contact info</WSText.Heading3>
          <WSButton.Link
            onClick={() => {
              openModal(EDIT_CONTACT_INFO_MODAL);
            }}
          >
            Edit
          </WSButton.Link>
        </WSFlexBox.CenterY>

        <WSText.Label mb="XS" color="grey600">
          Client contact email
        </WSText.Label>
        <WSText mb="M">{memberClient.emailTo}</WSText>

        <WSText.Label mb="XS" color="grey600">
          Additional emails
        </WSText.Label>
        <WSText>
          {memberClient.emailCC && memberClient.emailCC.length > 0
            ? memberClient.emailCC.join(", ")
            : "–"}
        </WSText>
      </SidePanelSection>

      {memberClient.eligibilityRequirements &&
        memberClient.eligibilityRequirements.length > 0 && (
          <SidePanelSection>
            <ClientEligibilityRequirements
              memberClientId={memberClient.memberClientId}
            />
          </SidePanelSection>
        )}

      {(memberClient.internationalWireAccount?.routingNumber ||
        memberClient.achCreditAccount.routingNumber) && (
        <SidePanelSection>
          <WSText.Heading3 mb="S">
            Wire transfers{" "}
            <Tooltip
              trigger={
                <WSIcon
                  name="info"
                  ml="XS"
                  color="grey600"
                  className={styles.icon}
                />
              }
              showInfoIcon={false}
              toolTipClassName={styles.toolTipContent}
              arrowClassName={styles.arrow}
              placement="right"
            >
              <WSText.LabelSmall color="grey700">
                This is the secure payment gateway setup for this client that
                routes money to your direct deposit without you having to
                provide your own banking info.
              </WSText.LabelSmall>
            </Tooltip>
          </WSText.Heading3>

          <WSFlexBox.CenterY wrap="nowrap" justify="space-between" mb="S">
            <WSElement>
              <WSText.Label mb="XS" color="grey600">
                Bank name
              </WSText.Label>
              <WSText>
                {isInternationalBankDetailsVisible
                  ? memberClient.internationalWireAccount.bankName
                  : memberClient.achCreditAccount.bankName}
              </WSText>
            </WSElement>
            <WSButton.Link
              onClick={() => {
                copyText(
                  isInternationalBankDetailsVisible
                    ? memberClient.internationalWireAccount.bankName
                    : memberClient.achCreditAccount.bankName,
                  "Bank name copied to clipboard"
                );
              }}
            >
              Copy
            </WSButton.Link>
          </WSFlexBox.CenterY>

          {(isInternationalBankDetailsVisible
            ? memberClient.internationalWireAccount.routingNumber
            : memberClient.achCreditAccount.routingNumber) && (
            <WSFlexBox.CenterY wrap="nowrap" justify="space-between" mb="S">
              <WSElement>
                <WSText.Label mb="XS" color="grey600">
                  Routing number
                </WSText.Label>
                <WSText>
                  {isInternationalBankDetailsVisible
                    ? memberClient.internationalWireAccount.routingNumber
                    : memberClient.achCreditAccount.routingNumber}
                </WSText>
              </WSElement>
              <WSButton.Link
                onClick={() => {
                  copyText(
                    isInternationalBankDetailsVisible
                      ? (memberClient.internationalWireAccount
                          .routingNumber as string)
                      : (memberClient.achCreditAccount.routingNumber as string),
                    "Routing number copied to clipboard"
                  );
                }}
              >
                Copy
              </WSButton.Link>
            </WSFlexBox.CenterY>
          )}

          <WSFlexBox.CenterY wrap="nowrap" justify="space-between" mb="S">
            <WSElement>
              <WSText.Label mb="XS" color="grey600">
                Account number
              </WSText.Label>
              <WSText>
                {isInternationalBankDetailsVisible
                  ? memberClient.internationalWireAccount.accountNumber
                  : memberClient.achCreditAccount.accountNumber}
              </WSText>
            </WSElement>
            <WSButton.Link
              onClick={() => {
                copyText(
                  isInternationalBankDetailsVisible
                    ? memberClient.internationalWireAccount.accountNumber
                    : memberClient.achCreditAccount.accountNumber,
                  "Account number copied to clipboard"
                );
              }}
            >
              Copy
            </WSButton.Link>
          </WSFlexBox.CenterY>

          {((isInternationalBankDetailsVisible &&
            memberClient.internationalWireAccount.swiftCode) ||
            (!isInternationalBankDetailsVisible &&
              memberClient.achCreditAccount.swiftCode)) && (
            <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
              <WSElement>
                <WSText.Label mb="XS" color="grey600">
                  SWIFT
                </WSText.Label>
                <WSText>
                  {isInternationalBankDetailsVisible
                    ? memberClient.internationalWireAccount.swiftCode
                    : memberClient.achCreditAccount.swiftCode}
                </WSText>
              </WSElement>

              <WSButton.Link
                onClick={() => {
                  copyText(
                    (isInternationalBankDetailsVisible
                      ? memberClient.internationalWireAccount.swiftCode
                      : memberClient.achCreditAccount.swiftCode) || "",
                    "SWIFT code copied to clipboard"
                  );
                }}
              >
                Copy
              </WSButton.Link>
            </WSFlexBox.CenterY>
          )}

          {memberClient.internationalWireAccount && (
            <WSButton.Link
              type="button"
              mt="M"
              onClick={() => {
                setIsInternationalBankDetailsVisible(value => !value);
              }}
            >
              {isInternationalBankDetailsVisible
                ? "View domestic wire details"
                : "View international wire details"}
            </WSButton.Link>
          )}
        </SidePanelSection>
      )}

      {memberClient.form1099Balances?.["2021"] && (
        <SidePanelSection>
          <WSText.Heading3 mb="S">Tax information 2021</WSText.Heading3>

          <WSElement mb="S">
            <WSText.Label mb="XS" color="grey600">
              On Platform Gross Income
            </WSText.Label>
            <WSText formatMoney>
              {memberClient.form1099Balances["2021"].platformIncome}
            </WSText>
          </WSElement>

          <WSElement mb="S">
            <WSText.Label mb="XS" color="grey600">
              Off Platform Adjustments
            </WSText.Label>
            <WSText formatMoney>
              {memberClient.form1099Balances["2021"].adjustments}
            </WSText>
          </WSElement>

          <WSElement mb="S">
            <WSText.Label mb="XS" color="grey600">
              Send 1099 Filing
            </WSText.Label>
            <WSText>
              {(memberClient.form1099Balances["2021"] as any).issue1099
                ? "Yes"
                : "No"}
            </WSText>
          </WSElement>
        </SidePanelSection>
      )}

      {isQuickbooksActive && (
        <>
          <EditClientIntegrationsModal
            memberClientId={memberClient.memberClientId}
          />
          <SidePanelSection data-testid="clientIntegrationMapping">
            <WSFlexBox.CenterY wrap="nowrap" justify="space-between" mb="S">
              <WSText.Heading3>Integrations Mappings</WSText.Heading3>
              <WSButton.Link
                onClick={() => {
                  openModal(EDIT_CLIENT_INTEGRATIONS_MODAL);
                }}
              >
                Edit
              </WSButton.Link>
            </WSFlexBox.CenterY>

            <WSText.Label mb="XS" color="grey600">
              QBO Customer
            </WSText.Label>
            <WSText mb="S">
              {mappedCustomer
                ? getIntegrationUserSelectLabel(mappedCustomer)
                : "Not mapped"}
            </WSText>
            <WSText.Label mb="XS" color="grey600">
              Default QBO Item
            </WSText.Label>
            <WSText mb="S">
              {mappedItem
                ? mappedItem.fullyQualifiedName
                : `Not mapped, use default "${defaultItem?.fullyQualifiedName}"`}
            </WSText>
          </SidePanelSection>

          <QuickbooksHistory
            Wrapper={SidePanelSection}
            entityId={memberClient.memberClientId}
          />
        </>
      )}

      {documents && (
        <DocumentsSection
          documents={documents}
          showAmounts
          testid="documents-section"
        />
      )}

      <SidePanelSection>
        <WSButton.Link
          color="red500"
          onClick={() => {
            updateMemberClient(
              {
                status: MemberClientStatus.Inactive
              },
              {
                onSuccess: () => {
                  history.push(backPath);
                }
              }
            );
          }}
          loading={updateMemberClientMeta.isLoading}
          name="deleteClient"
        >
          Delete client
        </WSButton.Link>
      </SidePanelSection>

      <EditContactInfoModal memberClientId={memberClient.memberClientId} />
      <EditContactNameModal memberClientId={memberClient.memberClientId} />
    </>
  );
};
